// ==========================================================================
// SYSTEM SETTINGS & ROLE PERMISSIONS MODULE
// WuRuPayroll - Company configuration, authorized payors & RBAC permissions
// ==========================================================================

import { doc, setDoc } from 'firebase/firestore';
import { db, saveCompanySettings } from '../firebase-db.js';
import { showToastNotification, toast, showConfirm } from '../utils/toast.js';
import { escapeHtml, escapeAttr, escapeJsParam } from '../utils/sanitize.js';
import { switchReportTab } from './reports.js';

// Safe proxy for global application state
const state = new Proxy({}, {
  get(target, prop) {
    const s = (typeof window !== 'undefined' && window.state) ? window.state : (globalThis.state || target);
    return s[prop];
  },
  set(target, prop, value) {
    const s = (typeof window !== 'undefined' && window.state) ? window.state : (globalThis.state || target);
    s[prop] = value;
    return true;
  }
});

// Safe wrappers for app routing, context and permissions
const navigateTo = (page) => {
  if (typeof window !== 'undefined' && window.navigateTo) {
    return window.navigateTo(page);
  }
};

const checkPermission = (action) => {
  if (typeof window !== 'undefined' && window.checkPermission) {
    return window.checkPermission(action);
  }
  return true;
};

function toggleDayWorkHours(day) {
  const cb = document.getElementById(`workday-${day}`);
  const label = document.getElementById(`dayoff-label-${day}`);
  if (cb && label) {
    label.textContent = cb.checked ? '' : ' (วันหยุด)';
  }
}

function toggleDiligenceAllowanceSection() {
  const cb = document.getElementById('set-enableDiligence');
  const sec = document.getElementById('diligence-settings-section');
  const amountInput = document.getElementById('set-diligenceAmount');
  if (cb && sec) {
    if (cb.checked) {
      sec.style.opacity = '1';
      sec.style.pointerEvents = 'auto';
      if (amountInput) amountInput.required = true;
    } else {
      sec.style.opacity = '0.5';
      sec.style.pointerEvents = 'none';
      if (amountInput) amountInput.required = false;
    }
  }
}

function renderSettingsPage() {
  document.getElementById('page-display-title').textContent = 'ตั้งค่าระบบบริษัท';
  document.getElementById('page-display-desc').textContent = `ปรับแต่งค่าตั้งต้นและค่าการคำนวณเงินเดือนสำหรับบริษัท ${state.activeCompanySettings.name}`;

  const wrapper = document.getElementById('page-content-wrapper');
  const settings = state.activeCompanySettings;
  const activeThemeLabel = settings.theme === 'peach-cream' ? 'โทนสีส้มโอรสครีม (Peach-Cream)' : 'โทนสีเทาสุดเท่ (Charcoal-Grey)';

  const makeHourSelect = (id, selectedVal) => {
    let options = '';
    for (let h = 0; h < 24; h++) {
      const val = String(h).padStart(2, '0');
      options += `<option value="${val}" ${val === selectedVal ? 'selected' : ''}>${val}</option>`;
    }
    return `<select id="${id}" style="padding:5px; font-size:0.8rem; border-radius:4px; border:1px solid var(--gray-300); width:48px; font-family:inherit; color:var(--text-main); background:var(--card-bg); cursor:pointer; text-align:center;">${options}</select>`;
  };

  const makeMinSelect = (id, selectedVal) => {
    let options = '';
    for (let m = 0; m < 60; m++) {
      const val = String(m).padStart(2, '0');
      options += `<option value="${val}" ${val === selectedVal ? 'selected' : ''}>${val}</option>`;
    }
    return `<select id="${id}" style="padding:5px; font-size:0.8rem; border-radius:4px; border:1px solid var(--gray-300); width:48px; font-family:inherit; color:var(--text-main); background:var(--card-bg); cursor:pointer; text-align:center;">${options}</select>`;
  };

  // Helper to parse working hours settings (handles '0', '8', 'OFF:08:00-17:00', '08:00-17:00', etc)
  const parseVal = (val) => {
    let str = String(val || '0').trim();
    let active = true;
    if (str.startsWith('OFF:')) {
      active = false;
      str = str.substring(4);
    }
    if (!str || str === '0') return { active: false, start: '08:00', end: '17:00' };
    const parts = str.split(/[-~]|to/i);
    if (parts.length === 2) {
      const cleanTime = (p) => {
        const clean = p.replace(/[^0-9:]/g, '').trim();
        if (clean.includes(':')) {
          const tp = clean.split(':');
          return `${String(parseInt(tp[0]) || 0).padStart(2, '0')}:${String(parseInt(tp[1]) || 0).padStart(2, '0')}`;
        }
        return `${String(parseInt(clean) || 0).padStart(2, '0')}:00`;
      };
      return { active, start: cleanTime(parts[0]), end: cleanTime(parts[1]) };
    }
    const num = parseFloat(str);
    if (!isNaN(num) && num > 0) {
      const endHr = 8 + num;
      const endHrStr = String(Math.floor(endHr) % 24).padStart(2, '0');
      const endMinStr = String(Math.round((endHr % 1) * 60)).padStart(2, '0');
      return { active: true, start: '08:00', end: `${endHrStr}:${endMinStr}` };
    }
    return { active: false, start: '08:00', end: '17:00' };
  };

  const mon = parseVal(settings.workHoursPerDay.monday);
  const tue = parseVal(settings.workHoursPerDay.tuesday);
  const wed = parseVal(settings.workHoursPerDay.wednesday);
  const thu = parseVal(settings.workHoursPerDay.thursday);
  const fri = parseVal(settings.workHoursPerDay.friday);
  const sat = parseVal(settings.workHoursPerDay.saturday);
  const sun = parseVal(settings.workHoursPerDay.sunday);


  wrapper.innerHTML = `
    <div class="glass-card">
      <h3 style="font-weight: 700; color: var(--primary-dark); margin-bottom: 20px; border-bottom:1px solid var(--gray-300); padding-bottom:10px;">🏢 ตั้งค่ารายละเอียดและเกณฑ์การเงินบริษัท</h3>
      <form id="system-settings-form">
        <div class="form-group">
          <label>ชื่อเรียกบริษัท (แสดงในหน้าหลักและสลิป) *</label>
          <input type="text" id="set-name" value="${escapeAttr(settings.name)}" required>
        </div>

        <div class="form-group">
          <label>Google Apps Script Web App URL (สำหรับอัปโหลดข้อมูลสำรองเข้า Google Drive เมื่อ Firebase Storage > 4.8 GB)</label>
          <input type="url" id="set-googleAppsScriptUrl" value="${escapeAttr(settings.googleAppsScriptUrl || 'https://script.google.com/macros/s/AKfycbzbMpQ7_dM6OY7QzxHaTHrem3ABx4K0uDIUGKM_A8qR9P3pvIxdJNF8Bl7rujQpQDJcZQ/exec')}" placeholder="https://script.google.com/macros/s/.../exec">
        </div>

        <div class="form-group">
          <label>ที่อยู่บริษัท *</label>
          <textarea id="set-address" required rows="2" style="font-family:inherit; resize:vertical; padding:12px 16px; border:1px solid var(--gray-300); border-radius:var(--border-radius-md); width:100%; outline:none; transition:border-color var(--transition-speed);">${settings.address || ''}</textarea>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label>เลขประจำตัวผู้เสียภาษีบริษัท (13 หลัก) *</label>
            <input type="text" id="set-taxId" value="${settings.taxId || ''}" maxlength="13" required>
          </div>
          <div class="form-group">
            <label>เบอร์โทรศัพท์ติดต่อบริษัท *</label>
            <input type="text" id="set-phone" value="${settings.phone || ''}" required>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label>ชุดสีตกแต่งระบบ (Theme Color)</label>
            <input type="text" value="${activeThemeLabel}" disabled style="background:var(--gray-200); font-weight:700;">
          </div>
          <div class="form-group">
            <label>หัก ณ ที่จ่ายพนักงานฝึกงาน/ฟรีแลนซ์ (%) *</label>
            <input type="number" id="set-taxRate" value="${settings.withholdingTaxRate}" required step="0.1">
          </div>
        </div>

        <div class="form-row" style="background:rgba(0,0,0,0.03); padding:15px; border-radius:var(--border-radius-sm); margin-bottom:20px;">
          <div class="form-group">
            <label>อัตราหักประกันสังคม (%) *</label>
            <input type="number" id="set-ssRate" value="${settings.socialSecurityRate}" required>
          </div>
          <div class="form-group">
            <label>ยอดสมทบประกันสังคมขั้นต่ำ (บาท/เดือน) *</label>
            <input type="number" id="set-ssMin" value="${settings.socialSecurityMin}" required step="0.01">
          </div>
          <div class="form-group">
            <label>ยอดสมทบประกันสังคมสูงสุด (เพดาน) (บาท/เดือน) *</label>
            <input type="number" id="set-ssMax" value="${settings.socialSecurityMax}" required step="0.01">
          </div>
        </div>

        <h4 style="font-weight:700; margin-top:20px; margin-bottom:15px; border-top:1px solid var(--gray-300); padding-top:20px;">🗓️ แหล่งข้อมูลวันหยุดนักขัตฤกษ์</h4>
        <div class="form-group" style="margin-bottom: 20px;">
          <label>API URL วันหยุด (ใช้ในปฏิทินรายเดือน) <span style="color:var(--text-sub); font-weight:normal;">*ใส่ {year} เพื่อให้ระบบแทนค่าปีอัตโนมัติ</span></label>
          <input type="url" id="set-publicHolidayApiUrl" value="${settings.publicHolidayApiUrl || 'https://thailandformats.com/api/v1/holidays/{year}'}" placeholder="https://thailandformats.com/api/v1/holidays/{year}">
          <small style="color: var(--primary-dark); display:block; margin-top:5px;">หมายเหตุ: แนะนำให้ใช้ Thailand Formats API หากว่างเปล่า ระบบจะใช้ค่าเริ่มต้นอัตโนมัติ เพื่อดึงข้อมูลวันหยุดมาแสดงเป็นข้อมูลอ้างอิง</small>
        </div>

        <div class="form-group" style="margin-bottom: 20px;">
          <label>ระยะเวลาอัปเดตข้อมูลวันหยุด (วัน) *</label>
          <input type="number" id="set-publicHolidaySyncDays" value="${settings.publicHolidaySyncDays !== undefined ? settings.publicHolidaySyncDays : 7}" required min="1">
          <small style="color: var(--text-sub); display:block; margin-top:5px;">ระบบจะดึงข้อมูลวันหยุดจาก API ภายนอกเพียง 1 ครั้งตามจำนวนวันที่กำหนด เพื่อลดความล่าช้าในการโหลดหน้าปฏิทิน</small>
        </div>

        <!-- Attendance / Leave toggles (Requirement 27) -->
        <h4 style="font-weight:700; margin-top:20px; margin-bottom:15px;">🌴 ตั้งค่าสิทธิ์การลาหยุดและการคำนวณ</h4>
        <div style="display:flex; flex-direction:column; gap:10px;">
          <div class="switch-container">
            <label class="switch">
              <input type="checkbox" id="set-sickLeave" ${settings.enableSickLeaveCalc ? 'checked' : ''}>
              <span class="slider"></span>
            </label>
            <span>🤒 เปิดใช้งานการคำนวณจ่ายเงินวันลาป่วย (จ่ายค่าจ้างให้ปกติในวันลาป่วย)</span>
          </div>

          <div class="switch-container">
            <label class="switch">
              <input type="checkbox" id="set-annualLeave" ${settings.enableAnnualLeaveCalc ? 'checked' : ''}>
              <span class="slider"></span>
            </label>
            <span>🌴 เปิดใช้งานการคำนวณจ่ายเงินวันลาพักร้อน (จ่ายค่าจ้างปกติในวันหยุดพักร้อน)</span>
          </div>
                <div class="switch-container">
                  <label class="switch">
                    <input type="checkbox" id="set-personalLeave" ${settings.enablePersonalLeaveCalc ? 'checked' : ''}>
                    <span class="slider"></span>
                  </label>
                  <span>💼 จ่ายค่าจ้างสำหรับวันลากิจ (Personal Leave - เฉพาะพนักงานเงินเดือน)</span>
                </div>
        </div>

        <div class="form-row" style="margin-top: 15px;">
          <div class="form-group">
            <label>ระบบวันหยุดประจำปีของบริษัท</label>
            <select id="set-holidaySystem" style="padding: 10px 14px; font-size: 0.9rem; border-radius: var(--border-radius-md); border: 1px solid var(--gray-300); outline: none; background: var(--card-bg); width: 100%; color: var(--text-main); cursor: pointer; font-family: inherit;">
              <option value="admin_defined" ${settings.holidaySystem === 'admin_defined' ? 'selected' : ''}>👤 ผู้บริหารเป็นคนกำหนดวันหยุดประจำปีให้</option>
              <option value="employee_selected" ${settings.holidaySystem === 'employee_selected' ? 'selected' : ''}>👥 ลูกน้องเป็นคนเลือกวันหยุดประจำปีเอง</option>
            </select>
          </div>
          <div class="form-group">
            <label>จำนวนวันหยุดประจำปีโควตาของบริษัท (วัน/ปี) *</label>
            <input type="number" id="set-companyHolidaysQuota" value="${settings.companyHolidaysQuota !== undefined ? settings.companyHolidaysQuota : 13}" required min="0" style="padding: 10px 14px; font-size: 0.9rem; border-radius: var(--border-radius-md); border: 1px solid var(--gray-300); outline: none; background: var(--card-bg); width: 100%; color: var(--text-main); font-family: inherit;">
          </div>
        </div>

        <div class="switch-container" style="margin-top: 15px;">
          <label class="switch">
            <input type="checkbox" id="set-allowEmployeesToViewOthersLeave" ${settings.allowEmployeesToViewOthersLeave !== false ? 'checked' : ''}>
            <span class="slider"></span>
          </label>
          <span>👥 อนุญาตให้พนักงานทั่วไปดูวันหยุด/วันลาของเพื่อนร่วมงานคนอื่นบนปฏิทิน</span>
        </div>

        <!-- Partial Day Deduction Rounding -->
        <h4 style="font-weight:700; margin-top:25px; margin-bottom:15px; border-top:1px solid var(--gray-300); padding-top:20px;">🔢 การแสดงผลยอดจริง/วัน (สำหรับพนักงานรายวัน/ฟรีแลนซ์)</h4>
        
        <div class="form-group" style="margin-bottom: 20px;">
          <label>รูปแบบการคำนวณและแสดงผลเมื่อทำงานไม่เต็มวัน</label>
          <select id="set-partialDayRounding" style="padding: 10px 14px; font-size: 0.9rem; border-radius: var(--border-radius-md); border: 1px solid var(--gray-300); outline: none; background: var(--card-bg); width: 100%; color: var(--text-main); cursor: pointer; font-family: inherit;">
            <option value="round" ${settings.partialDayRounding !== 'decimal' ? 'selected' : ''}>ปัดเศษเป็นจำนวนเต็ม (เช่น 400 บาท)</option>
            <option value="decimal" ${settings.partialDayRounding === 'decimal' ? 'selected' : ''}>แสดงทศนิยม 2 ตำแหน่ง (เช่น 400.25 บาท)</option>
          </select>
        </div>

        <!-- Diligence settings -->
        <h4 style="font-weight:700; margin-top:25px; margin-bottom:15px; border-top:1px solid var(--gray-300); padding-top:20px;">🏆 ตั้งค่าเกณฑ์และยอดเงินเบี้ยขยันประจำเดือน</h4>
        
        <div class="switch-container" style="margin-bottom: 20px;">
          <label class="switch">
            <input type="checkbox" id="set-enableDiligence" onchange="toggleDiligenceAllowanceSection()" ${settings.enableDiligenceAllowance !== false ? 'checked' : ''}>
            <span class="slider"></span>
          </label>
          <span>🏆 เปิดใช้งานระบบคำนวณเบี้ยขยันประจำเดือน</span>
        </div>

        <div id="diligence-settings-section" style="${settings.enableDiligenceAllowance !== false ? '' : 'opacity: 0.5; pointer-events: none;'} transition: opacity 0.3s ease;">
          <div class="form-group" style="margin-bottom: 20px;">
            <label>จำนวนเงินเบี้ยขยันเริ่มต้น (บาท) *</label>
            <input type="number" id="set-diligenceAmount" value="${settings.diligenceAllowanceAmount !== undefined ? settings.diligenceAllowanceAmount : 1000}" ${settings.enableDiligenceAllowance !== false ? 'required' : ''}>
          </div>

          <label style="font-weight:600; display:block; margin-bottom:10px;">เกณฑ์ที่ต้องปฏิบัติตามเพื่อรับเบี้ยขยัน (ต้องไม่มีรายการดังต่อไปนี้):</label>
          <div style="display:flex; flex-direction:column; gap:10px; margin-bottom:20px; background:rgba(0,0,0,0.02); padding:15px; border-radius:var(--border-radius-sm); border:1px solid var(--gray-300);">
            <div class="switch-container" style="margin-bottom: 0;">
              <label class="switch">
                <input type="checkbox" id="set-diligenceNoLate" ${settings.diligenceRequireNoLate !== false ? 'checked' : ''}>
                <span class="slider"></span>
              </label>
              <span>⏰ ต้องไม่มาสายเลยสักนาทีเดียว (สาย = ตัดสิทธิ์)</span>
            </div>
            
            <div class="switch-container" style="margin-bottom: 0;">
              <label class="switch">
                <input type="checkbox" id="set-diligenceNoAbsence" ${settings.diligenceRequireNoAbsence !== false ? 'checked' : ''}>
                <span class="slider"></span>
              </label>
              <span>Absence: ต้องไม่มีวันขาดงาน (วันหยุดที่ไม่ได้แจ้งหรือหยุดโดยไม่มีการอนุมัติ)</span>
            </div>

            <div class="switch-container" style="margin-bottom: 0;">
              <label class="switch">
                <input type="checkbox" id="set-diligenceNoSick" ${settings.diligenceRequireNoSickLeave !== false ? 'checked' : ''}>
                <span class="slider"></span>
              </label>
              <span>Sick Leave: ต้องไม่มีวันลาป่วย</span>
            </div>

            <div class="switch-container" style="margin-bottom: 0;">
              <label class="switch">
                <input type="checkbox" id="set-diligenceNoAnnual" ${settings.diligenceRequireNoAnnualLeave !== false ? 'checked' : ''}>
                <span class="slider"></span>
              </label>
              <span>Annual Leave: ต้องไม่มีวันลาพักร้อน</span>
            </div>

            <div class="switch-container" style="margin-bottom: 0;">
              <label class="switch">
                <input type="checkbox" id="set-diligenceNoPersonal" ${settings.diligenceRequireNoPersonalLeave !== false ? 'checked' : ''}>
                <span class="slider"></span>
              </label>
              <span>Personal Leave: ต้องไม่มีวันลากิจ</span>
            </div>
          </div>
        </div>

        <!-- Hourly settings per day (Requirement 8) -->
        <h4 style="font-weight:700; margin-top:25px; margin-bottom:15px; border-top:1px solid var(--gray-300); padding-top:20px;">⏰ เวลาการทำงานต่อวัน</h4>
        <p style="font-size:0.82rem; color:var(--text-sub); margin-bottom:15px;">ระบุช่วงเวลาทำงานปกติในแต่ละวันเพื่อใช้เป็นเกณฑ์คำนวณสายรายนาที หากวันใดเป็นวันหยุดบริษัทปกติให้สับสวิตช์ปิด (เวลาเริ่มงานและเลิกงานจะยังคงเปิดให้แก้ไขได้ปกติเพื่อใช้อ้างอิงสำหรับการทำงนพิเศษหรือสลับวันหยุด)</p>
        <div style="display:grid; grid-template-columns: repeat(auto-fit, minmax(135px, 1fr)); gap:12px; margin-bottom:20px;">
          <!-- Monday -->
          <div style="background:var(--gray-200); border:1px solid var(--gray-300); padding:10px; border-radius:var(--border-radius-md); display:flex; flex-direction:column; gap:8px;">
            <div style="display:flex; justify-content:space-between; align-items:center; border-bottom:1px solid var(--gray-300); padding-bottom:6px; margin-bottom:4px;">
              <strong style="color:var(--primary-dark); font-size:0.85rem;">วันจันทร์ <span id="dayoff-label-monday" style="color:var(--text-sub); font-size:0.75rem; font-weight:normal;">${mon.active ? '' : ' (วันหยุด)'}</span></strong>
              <label class="switch switch-sm" style="transform:scale(0.85); transform-origin:right;">
                <input type="checkbox" id="workday-monday" onchange="toggleDayWorkHours('monday')" ${mon.active ? 'checked' : ''}>
                <span class="slider"></span>
              </label>
            </div>
            <div id="time-inputs-monday" style="display:flex; flex-direction:column; gap:6px;">
              <div style="display:flex; align-items:center; gap:4px; justify-content:space-between;">
                <span style="font-size:0.75rem; color:var(--text-sub); min-width:35px;">เริ่มงาน</span>
                <div style="display:flex; align-items:center; gap:2px;">
                  ${makeHourSelect('start-hr-monday', mon.start.split(':')[0])}
                  <span style="font-weight:bold; font-size:0.8rem;">:</span>
                  ${makeMinSelect('start-min-monday', mon.start.split(':')[1])}
                </div>
              </div>
              <div style="display:flex; align-items:center; gap:4px; justify-content:space-between;">
                <span style="font-size:0.75rem; color:var(--text-sub); min-width:35px;">เลิกงาน</span>
                <div style="display:flex; align-items:center; gap:2px;">
                  ${makeHourSelect('end-hr-monday', mon.end.split(':')[0])}
                  <span style="font-weight:bold; font-size:0.8rem;">:</span>
                  ${makeMinSelect('end-min-monday', mon.end.split(':')[1])}
                </div>
              </div>
            </div>
          </div>
          <!-- Tuesday -->
          <div style="background:var(--gray-200); border:1px solid var(--gray-300); padding:10px; border-radius:var(--border-radius-md); display:flex; flex-direction:column; gap:8px;">
            <div style="display:flex; justify-content:space-between; align-items:center; border-bottom:1px solid var(--gray-300); padding-bottom:6px; margin-bottom:4px;">
              <strong style="color:var(--primary-dark); font-size:0.85rem;">วันอังคาร <span id="dayoff-label-tuesday" style="color:var(--text-sub); font-size:0.75rem; font-weight:normal;">${tue.active ? '' : ' (วันหยุด)'}</span></strong>
              <label class="switch switch-sm" style="transform:scale(0.85); transform-origin:right;">
                <input type="checkbox" id="workday-tuesday" onchange="toggleDayWorkHours('tuesday')" ${tue.active ? 'checked' : ''}>
                <span class="slider"></span>
              </label>
            </div>
            <div id="time-inputs-tuesday" style="display:flex; flex-direction:column; gap:6px;">
              <div style="display:flex; align-items:center; gap:4px; justify-content:space-between;">
                <span style="font-size:0.75rem; color:var(--text-sub); min-width:35px;">เริ่มงาน</span>
                <div style="display:flex; align-items:center; gap:2px;">
                  ${makeHourSelect('start-hr-tuesday', tue.start.split(':')[0])}
                  <span style="font-weight:bold; font-size:0.8rem;">:</span>
                  ${makeMinSelect('start-min-tuesday', tue.start.split(':')[1])}
                </div>
              </div>
              <div style="display:flex; align-items:center; gap:4px; justify-content:space-between;">
                <span style="font-size:0.75rem; color:var(--text-sub); min-width:35px;">เลิกงาน</span>
                <div style="display:flex; align-items:center; gap:2px;">
                  ${makeHourSelect('end-hr-tuesday', tue.end.split(':')[0])}
                  <span style="font-weight:bold; font-size:0.8rem;">:</span>
                  ${makeMinSelect('end-min-tuesday', tue.end.split(':')[1])}
                </div>
              </div>
            </div>
          </div>
          <!-- Wednesday -->
          <div style="background:var(--gray-200); border:1px solid var(--gray-300); padding:10px; border-radius:var(--border-radius-md); display:flex; flex-direction:column; gap:8px;">
            <div style="display:flex; justify-content:space-between; align-items:center; border-bottom:1px solid var(--gray-300); padding-bottom:6px; margin-bottom:4px;">
              <strong style="color:var(--primary-dark); font-size:0.85rem;">วันพุธ <span id="dayoff-label-wednesday" style="color:var(--text-sub); font-size:0.75rem; font-weight:normal;">${wed.active ? '' : ' (วันหยุด)'}</span></strong>
              <label class="switch switch-sm" style="transform:scale(0.85); transform-origin:right;">
                <input type="checkbox" id="workday-wednesday" onchange="toggleDayWorkHours('wednesday')" ${wed.active ? 'checked' : ''}>
                <span class="slider"></span>
              </label>
            </div>
            <div id="time-inputs-wednesday" style="display:flex; flex-direction:column; gap:6px;">
              <div style="display:flex; align-items:center; gap:4px; justify-content:space-between;">
                <span style="font-size:0.75rem; color:var(--text-sub); min-width:35px;">เริ่มงาน</span>
                <div style="display:flex; align-items:center; gap:2px;">
                  ${makeHourSelect('start-hr-wednesday', wed.start.split(':')[0])}
                  <span style="font-weight:bold; font-size:0.8rem;">:</span>
                  ${makeMinSelect('start-min-wednesday', wed.start.split(':')[1])}
                </div>
              </div>
              <div style="display:flex; align-items:center; gap:4px; justify-content:space-between;">
                <span style="font-size:0.75rem; color:var(--text-sub); min-width:35px;">เลิกงาน</span>
                <div style="display:flex; align-items:center; gap:2px;">
                  ${makeHourSelect('end-hr-wednesday', wed.end.split(':')[0])}
                  <span style="font-weight:bold; font-size:0.8rem;">:</span>
                  ${makeMinSelect('end-min-wednesday', wed.end.split(':')[1])}
                </div>
              </div>
            </div>
          </div>
          <!-- Thursday -->
          <div style="background:var(--gray-200); border:1px solid var(--gray-300); padding:10px; border-radius:var(--border-radius-md); display:flex; flex-direction:column; gap:8px;">
            <div style="display:flex; justify-content:space-between; align-items:center; border-bottom:1px solid var(--gray-300); padding-bottom:6px; margin-bottom:4px;">
              <strong style="color:var(--primary-dark); font-size:0.85rem;">วันพฤหัสบดี <span id="dayoff-label-thursday" style="color:var(--text-sub); font-size:0.75rem; font-weight:normal;">${thu.active ? '' : ' (วันหยุด)'}</span></strong>
              <label class="switch switch-sm" style="transform:scale(0.85); transform-origin:right;">
                <input type="checkbox" id="workday-thursday" onchange="toggleDayWorkHours('thursday')" ${thu.active ? 'checked' : ''}>
                <span class="slider"></span>
              </label>
            </div>
            <div id="time-inputs-thursday" style="display:flex; flex-direction:column; gap:6px;">
              <div style="display:flex; align-items:center; gap:4px; justify-content:space-between;">
                <span style="font-size:0.75rem; color:var(--text-sub); min-width:35px;">เริ่มงาน</span>
                <div style="display:flex; align-items:center; gap:2px;">
                  ${makeHourSelect('start-hr-thursday', thu.start.split(':')[0])}
                  <span style="font-weight:bold; font-size:0.8rem;">:</span>
                  ${makeMinSelect('start-min-thursday', thu.start.split(':')[1])}
                </div>
              </div>
              <div style="display:flex; align-items:center; gap:4px; justify-content:space-between;">
                <span style="font-size:0.75rem; color:var(--text-sub); min-width:35px;">เลิกงาน</span>
                <div style="display:flex; align-items:center; gap:2px;">
                  ${makeHourSelect('end-hr-thursday', thu.end.split(':')[0])}
                  <span style="font-weight:bold; font-size:0.8rem;">:</span>
                  ${makeMinSelect('end-min-thursday', thu.end.split(':')[1])}
                </div>
              </div>
            </div>
          </div>
          <!-- Friday -->
          <div style="background:var(--gray-200); border:1px solid var(--gray-300); padding:10px; border-radius:var(--border-radius-md); display:flex; flex-direction:column; gap:8px;">
            <div style="display:flex; justify-content:space-between; align-items:center; border-bottom:1px solid var(--gray-300); padding-bottom:6px; margin-bottom:4px;">
              <strong style="color:var(--primary-dark); font-size:0.85rem;">วันศุกร์ <span id="dayoff-label-friday" style="color:var(--text-sub); font-size:0.75rem; font-weight:normal;">${fri.active ? '' : ' (วันหยุด)'}</span></strong>
              <label class="switch switch-sm" style="transform:scale(0.85); transform-origin:right;">
                <input type="checkbox" id="workday-friday" onchange="toggleDayWorkHours('friday')" ${fri.active ? 'checked' : ''}>
                <span class="slider"></span>
              </label>
            </div>
            <div id="time-inputs-friday" style="display:flex; flex-direction:column; gap:6px;">
              <div style="display:flex; align-items:center; gap:4px; justify-content:space-between;">
                <span style="font-size:0.75rem; color:var(--text-sub); min-width:35px;">เริ่มงาน</span>
                <div style="display:flex; align-items:center; gap:2px;">
                  ${makeHourSelect('start-hr-friday', fri.start.split(':')[0])}
                  <span style="font-weight:bold; font-size:0.8rem;">:</span>
                  ${makeMinSelect('start-min-friday', fri.start.split(':')[1])}
                </div>
              </div>
              <div style="display:flex; align-items:center; gap:4px; justify-content:space-between;">
                <span style="font-size:0.75rem; color:var(--text-sub); min-width:35px;">เลิกงาน</span>
                <div style="display:flex; align-items:center; gap:2px;">
                  ${makeHourSelect('end-hr-friday', fri.end.split(':')[0])}
                  <span style="font-weight:bold; font-size:0.8rem;">:</span>
                  ${makeMinSelect('end-min-friday', fri.end.split(':')[1])}
                </div>
              </div>
            </div>
          </div>
          <!-- Saturday -->
          <div style="background:var(--gray-200); border:1px solid var(--gray-300); padding:10px; border-radius:var(--border-radius-md); display:flex; flex-direction:column; gap:8px;">
            <div style="display:flex; justify-content:space-between; align-items:center; border-bottom:1px solid var(--gray-300); padding-bottom:6px; margin-bottom:4px;">
              <strong style="color:var(--primary-dark); font-size:0.85rem;">วันเสาร์ <span id="dayoff-label-saturday" style="color:var(--text-sub); font-size:0.75rem; font-weight:normal;">${sat.active ? '' : ' (วันหยุด)'}</span></strong>
              <label class="switch switch-sm" style="transform:scale(0.85); transform-origin:right;">
                <input type="checkbox" id="workday-saturday" onchange="toggleDayWorkHours('saturday')" ${sat.active ? 'checked' : ''}>
                <span class="slider"></span>
              </label>
            </div>
            <div id="time-inputs-saturday" style="display:flex; flex-direction:column; gap:6px;">
              <div style="display:flex; align-items:center; gap:4px; justify-content:space-between;">
                <span style="font-size:0.75rem; color:var(--text-sub); min-width:35px;">เริ่มงาน</span>
                <div style="display:flex; align-items:center; gap:2px;">
                  ${makeHourSelect('start-hr-saturday', sat.start.split(':')[0])}
                  <span style="font-weight:bold; font-size:0.8rem;">:</span>
                  ${makeMinSelect('start-min-saturday', sat.start.split(':')[1])}
                </div>
              </div>
              <div style="display:flex; align-items:center; gap:4px; justify-content:space-between;">
                <span style="font-size:0.75rem; color:var(--text-sub); min-width:35px;">เลิกงาน</span>
                <div style="display:flex; align-items:center; gap:2px;">
                  ${makeHourSelect('end-hr-saturday', sat.end.split(':')[0])}
                  <span style="font-weight:bold; font-size:0.8rem;">:</span>
                  ${makeMinSelect('end-min-saturday', sat.end.split(':')[1])}
                </div>
              </div>
            </div>
          </div>
          <!-- Sunday -->
          <div style="background:var(--gray-200); border:1px solid var(--gray-300); padding:10px; border-radius:var(--border-radius-md); display:flex; flex-direction:column; gap:8px;">
            <div style="display:flex; justify-content:space-between; align-items:center; border-bottom:1px solid var(--gray-300); padding-bottom:6px; margin-bottom:4px;">
              <strong style="color:var(--primary-dark); font-size:0.85rem;">วันอาทิตย์ <span id="dayoff-label-sunday" style="color:var(--text-sub); font-size:0.75rem; font-weight:normal;">${sun.active ? '' : ' (วันหยุด)'}</span></strong>
              <label class="switch switch-sm" style="transform:scale(0.85); transform-origin:right;">
                <input type="checkbox" id="workday-sunday" onchange="toggleDayWorkHours('sunday')" ${sun.active ? 'checked' : ''}>
                <span class="slider"></span>
              </label>
            </div>
            <div id="time-inputs-sunday" style="display:flex; flex-direction:column; gap:6px;">
              <div style="display:flex; align-items:center; gap:4px; justify-content:space-between;">
                <span style="font-size:0.75rem; color:var(--text-sub); min-width:35px;">เริ่มงาน</span>
                <div style="display:flex; align-items:center; gap:2px;">
                  ${makeHourSelect('start-hr-sunday', sun.start.split(':')[0])}
                  <span style="font-weight:bold; font-size:0.8rem;">:</span>
                  ${makeMinSelect('start-min-sunday', sun.start.split(':')[1])}
                </div>
              </div>
              <div style="display:flex; align-items:center; gap:4px; justify-content:space-between;">
                <span style="font-size:0.75rem; color:var(--text-sub); min-width:35px;">เลิกงาน</span>
                <div style="display:flex; align-items:center; gap:2px;">
                  ${makeHourSelect('end-hr-sunday', sun.end.split(':')[0])}
                  <span style="font-weight:bold; font-size:0.8rem;">:</span>
                  ${makeMinSelect('end-min-sunday', sun.end.split(':')[1])}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style="margin-top:30px; display:flex; justify-content:flex-end;">
          <button type="submit" class="btn btn-primary">💾 บันทึกการตั้งค่าระบบ</button>
        </div>
      </form>
    </div>

    <!-- Garbage Data Cleanup -->
    <div class="glass-card" style="margin-top: 20px;">
      <h3 style="font-weight: 700; color: var(--danger); margin-bottom: 20px; border-bottom:1px solid var(--gray-300); padding-bottom:10px;">🗑️ จัดการข้อมูลขยะ (Garbage Data)</h3>
      <p style="color: var(--text-sub); margin-bottom: 15px;">ฟังก์ชันนี้จะสแกนและลบข้อมูลตารางเงินเดือนในอดีตที่มีสถานะว่างเปล่า (ยอดเงินสุทธิเป็น 0 บาท และไม่มีการแก้ไขหรืออัปโหลดสลิปใดๆ) ซึ่งเกิดจากการคลิกเข้ามาดูเฉยๆ</p>
      <button type="button" class="btn btn-danger" onclick="cleanGarbageTimesheetsTrigger()">🧹 เริ่มล้างข้อมูลขยะ</button>
    </div>
  `;

  // Bind submit settings
  document.getElementById('system-settings-form').addEventListener('submit', async (e) => {
    e.preventDefault();

    const updatedSettings = {
      ...settings,
      name: document.getElementById('set-name').value.trim(),
      googleAppsScriptUrl: document.getElementById('set-googleAppsScriptUrl').value.trim(),
      publicHolidayApiUrl: document.getElementById('set-publicHolidayApiUrl').value.trim(),
      publicHolidaySyncDays: parseInt(document.getElementById('set-publicHolidaySyncDays').value || 7),
      address: document.getElementById('set-address').value.trim(),
      taxId: document.getElementById('set-taxId').value.trim(),
      phone: document.getElementById('set-phone').value.trim(),
      withholdingTaxRate: parseFloat(document.getElementById('set-taxRate').value || 0),
      socialSecurityRate: parseFloat(document.getElementById('set-ssRate').value || 0),
      socialSecurityMin: parseFloat(document.getElementById('set-ssMin').value || 0),
      socialSecurityMax: parseFloat(document.getElementById('set-ssMax').value || 0),
      enableSickLeaveCalc: document.getElementById('set-sickLeave').checked,
      enableAnnualLeaveCalc: document.getElementById('set-annualLeave').checked,
        enablePersonalLeaveCalc: document.getElementById('set-personalLeave').checked,
      holidaySystem: document.getElementById('set-holidaySystem').value,
      companyHolidaysQuota: parseInt(document.getElementById('set-companyHolidaysQuota').value || 13),
      allowEmployeesToViewOthersLeave: document.getElementById('set-allowEmployeesToViewOthersLeave').checked,
      partialDayRounding: document.getElementById('set-partialDayRounding').value,
      enableDiligenceAllowance: document.getElementById('set-enableDiligence').checked,
      diligenceAllowanceAmount: parseFloat(document.getElementById('set-diligenceAmount').value || 0),
      diligenceRequireNoLate: document.getElementById('set-diligenceNoLate').checked,
      diligenceRequireNoAbsence: document.getElementById('set-diligenceNoAbsence').checked,
      diligenceRequireNoSickLeave: document.getElementById('set-diligenceNoSick').checked,
      diligenceRequireNoAnnualLeave: document.getElementById('set-diligenceNoAnnual').checked,
      diligenceRequireNoPersonalLeave: document.getElementById('set-diligenceNoPersonal').checked,
      workHoursPerDay: {
        monday: document.getElementById('workday-monday').checked
          ? `${document.getElementById('start-hr-monday').value}:${document.getElementById('start-min-monday').value}-${document.getElementById('end-hr-monday').value}:${document.getElementById('end-min-monday').value}`
          : `OFF:${document.getElementById('start-hr-monday').value}:${document.getElementById('start-min-monday').value}-${document.getElementById('end-hr-monday').value}:${document.getElementById('end-min-monday').value}`,
        tuesday: document.getElementById('workday-tuesday').checked
          ? `${document.getElementById('start-hr-tuesday').value}:${document.getElementById('start-min-tuesday').value}-${document.getElementById('end-hr-tuesday').value}:${document.getElementById('end-min-tuesday').value}`
          : `OFF:${document.getElementById('start-hr-tuesday').value}:${document.getElementById('start-min-tuesday').value}-${document.getElementById('end-hr-tuesday').value}:${document.getElementById('end-min-tuesday').value}`,
        wednesday: document.getElementById('workday-wednesday').checked
          ? `${document.getElementById('start-hr-wednesday').value}:${document.getElementById('start-min-wednesday').value}-${document.getElementById('end-hr-wednesday').value}:${document.getElementById('end-min-wednesday').value}`
          : `OFF:${document.getElementById('start-hr-wednesday').value}:${document.getElementById('start-min-wednesday').value}-${document.getElementById('end-hr-wednesday').value}:${document.getElementById('end-min-wednesday').value}`,
        thursday: document.getElementById('workday-thursday').checked
          ? `${document.getElementById('start-hr-thursday').value}:${document.getElementById('start-min-thursday').value}-${document.getElementById('end-hr-thursday').value}:${document.getElementById('end-min-thursday').value}`
          : `OFF:${document.getElementById('start-hr-thursday').value}:${document.getElementById('start-min-thursday').value}-${document.getElementById('end-hr-thursday').value}:${document.getElementById('end-min-thursday').value}`,
        friday: document.getElementById('workday-friday').checked
          ? `${document.getElementById('start-hr-friday').value}:${document.getElementById('start-min-friday').value}-${document.getElementById('end-hr-friday').value}:${document.getElementById('end-min-friday').value}`
          : `OFF:${document.getElementById('start-hr-friday').value}:${document.getElementById('start-min-friday').value}-${document.getElementById('end-hr-friday').value}:${document.getElementById('end-min-friday').value}`,
        saturday: document.getElementById('workday-saturday').checked
          ? `${document.getElementById('start-hr-saturday').value}:${document.getElementById('start-min-saturday').value}-${document.getElementById('end-hr-saturday').value}:${document.getElementById('end-min-saturday').value}`
          : `OFF:${document.getElementById('start-hr-saturday').value}:${document.getElementById('start-min-saturday').value}-${document.getElementById('end-hr-saturday').value}:${document.getElementById('end-min-saturday').value}`,
        sunday: document.getElementById('workday-sunday').checked
          ? `${document.getElementById('start-hr-sunday').value}:${document.getElementById('start-min-sunday').value}-${document.getElementById('end-hr-sunday').value}:${document.getElementById('end-min-sunday').value}`
          : `OFF:${document.getElementById('start-hr-sunday').value}:${document.getElementById('start-min-sunday').value}-${document.getElementById('end-hr-sunday').value}:${document.getElementById('end-min-sunday').value}`
      }
    };

    try {
      // เปรียบเทียบหาค่าที่มีการเปลี่ยนแปลงเพื่อนำไปแสดงผลใน Notification
      const labelMapping = {
        name: 'ชื่อบริษัท',
        googleAppsScriptUrl: 'Google Apps Script URL',
        publicHolidayApiUrl: 'API URL วันหยุดนักขัตฤกษ์',
        publicHolidaySyncDays: 'ระยะเวลาอัปเดตวันหยุด (วัน)',
        address: 'ที่อยู่บริษัท',
        taxId: 'เลขประจำตัวผู้เสียภาษี',
        phone: 'เบอร์โทรศัพท์',
        withholdingTaxRate: 'อัตราหัก ณ ที่จ่าย (%)',
        socialSecurityRate: 'อัตราหักประกันสังคม (%)',
        socialSecurityMin: 'ฐานประกันสังคมขั้นต่ำ',
        socialSecurityMax: 'เพดานประกันสังคมสูงสุด',
        enableSickLeaveCalc: 'คำนวณจ่ายเงินวันลาป่วย',
        enableAnnualLeaveCalc: 'คำนวณจ่ายเงินวันลาพักร้อน',
    enablePersonalLeaveCalc: 'คำนวณเงินวันลากิจ',
        holidaySystem: 'ระบบวันหยุดประจำปี',
        enableDiligenceAllowance: 'ระบบเบี้ยขยันประจำเดือน',
        diligenceAllowanceAmount: 'เงินเบี้ยขยันเริ่มต้น',
        diligenceRequireNoLate: 'เกณฑ์เบี้ยขยัน: ห้ามสาย',
        diligenceRequireNoAbsence: 'เกณฑ์เบี้ยขยัน: ห้ามขาดงาน',
        diligenceRequireNoSickLeave: 'เกณฑ์เบี้ยขยัน: ห้ามลาป่วย',
        diligenceRequireNoAnnualLeave: 'เกณฑ์เบี้ยขยัน: ห้ามลาพักร้อน',
        diligenceRequireNoPersonalLeave: 'เกณฑ์เบี้ยขยัน: ห้ามลากิจ'
      };

      const dayMapping = {
        monday: 'วันจันทร์',
        tuesday: 'วันอังคาร',
        wednesday: 'วันพุธ',
        thursday: 'วันพฤหัสบดี',
        friday: 'วันศุกร์',
        saturday: 'วันเสาร์',
        sunday: 'วันอาทิตย์'
      };

      const changes = [];

      for (const key in labelMapping) {
        const oldVal = settings[key];
        const newVal = updatedSettings[key];
        if (oldVal !== newVal) {
          let oldDisplay = oldVal;
          let newDisplay = newVal;
          if (typeof oldVal === 'boolean') {
            oldDisplay = oldVal ? '✅ เปิด' : '❌ ปิด';
            newDisplay = newVal ? '✅ เปิด' : '❌ ปิด';
          } else if (key === 'holidaySystem') {
            oldDisplay = oldVal === 'admin_defined' ? '👤 ผู้บริหารกำหนดให้' : '👥 ลูกน้องเลือกเอง';
            newDisplay = newVal === 'admin_defined' ? '👤 ผู้บริหารกำหนดให้' : '👥 ลูกน้องเลือกเอง';
          } else if (key.endsWith('Rate')) {
            oldDisplay = `${oldVal}%`;
            newDisplay = `${newVal}%`;
          } else if (key.includes('Amount') || key.includes('Min') || key.includes('Max')) {
            oldDisplay = `${parseFloat(oldVal || 0).toLocaleString()} บาท`;
            newDisplay = `${parseFloat(newVal || 0).toLocaleString()} บาท`;
          }
          changes.push(`• <strong>${labelMapping[key]}:</strong> ${oldDisplay} ➡️ ${newDisplay}`);
        }
      }

      if (settings.workHoursPerDay && updatedSettings.workHoursPerDay) {
        for (const day in dayMapping) {
          const oldVal = settings.workHoursPerDay[day];
          const newVal = updatedSettings.workHoursPerDay[day];
          if (oldVal !== newVal) {
            const formatWorkHours = (val) => {
              if (!val) return 'ไม่ได้ตั้งค่า';
              if (val.startsWith('OFF:')) {
                return `วันหยุด (${val.substring(4)})`;
              }
              return `วันทำงาน (${val})`;
            };
            changes.push(`• <strong>เวลาทำงาน${dayMapping[day]}:</strong> ${formatWorkHours(oldVal)} ➡️ ${formatWorkHours(newVal)}`);
          }
        }
      }

      await saveCompanySettings(state.activeCompanyId, updatedSettings);

      let messageHtml = '';
      if (changes.length > 0) {
        messageHtml = `
          <div style="margin-top: 5px; border-top: 1px solid var(--gray-300); padding-top: 8px;">
            <strong style="color: var(--primary-dark); display: block; margin-bottom: 5px; font-size: 0.85rem;">รายการที่บันทึกการเปลี่ยนแปลง:</strong>
            <div style="max-height: 180px; overflow-y: auto; font-size: 0.82rem; line-height: 1.6; display: flex; flex-direction: column; gap: 4px;">
              ${changes.join('<br>')}
            </div>
          </div>
        `;
      } else {
        messageHtml = `
          <div style="margin-top: 5px; border-top: 1px solid var(--gray-300); padding-top: 8px; font-size: 0.85rem; color: var(--text-sub);">
            (ไม่มีการเปลี่ยนแปลงค่าใดๆ)
          </div>
        `;
      }

      showToastNotification("💾 บันทึกข้อมูลลง Firebase สำเร็จ!", messageHtml);
      navigateTo('settings'); // Reload settings page context (virtual re-render)
    } catch (err) {
      console.error(err);
      alert("ไม่สามารถบันทึกการตั้งค่าได้");
    }
  });
}

// ==========================================================================
// AUTHORIZED PAYORS MANAGEMENT (Saves to Firestore Company Settings)
// ==========================================================================
function updateSelectedPayor(val) {
  state.selectedPayor = val;
};

function showManagePayorsModal() {
  let modalOverlay = document.getElementById('payors-management-modal-overlay');
  if (!modalOverlay) {
    modalOverlay = document.createElement('div');
    modalOverlay.id = 'payors-management-modal-overlay';
    modalOverlay.className = 'modal-overlay';
    document.body.appendChild(modalOverlay);
  }

  const payorsList = state.activeCompanySettings.authorizedPayors || [];

  let listHTML = '';
  if (payorsList.length === 0) {
    listHTML = '<p style="color:var(--text-sub); text-align:center; padding: 20px 0;">ยังไม่มีรายชื่อผู้อนุมัติในระบบ</p>';
  } else {
    payorsList.forEach((p, idx) => {
      listHTML += `
        <div style="display:flex; justify-content:space-between; align-items:center; background:var(--gray-200); padding:8px 15px; border-radius:var(--border-radius-sm); margin-bottom:8px;">
          <span style="font-weight:700; color:var(--text-main); font-size:0.85rem;">${escapeHtml(p)}</span>
          <button class="btn btn-danger btn-sm" onclick="deletePayorItem(${idx})" style="padding:4px 8px; font-size:0.75rem; width:auto; border-radius:4px;">🗑️ ลบ</button>
        </div>
      `;
    });
  }

  modalOverlay.innerHTML = `
    <div class="modal-container" style="max-width: 500px;">
      <div class="modal-header">
        <h3>จัดการรายชื่อผู้อนุมัติสั่งจ่าย</h3>
        <button class="btn-close-modal" onclick="document.getElementById('payors-management-modal-overlay').classList.remove('active')">&times;</button>
      </div>
      <div class="modal-body">
        <div style="margin-bottom: 20px; background:rgba(0,0,0,0.02); padding:15px; border-radius:6px; border:1px solid var(--gray-300);">
          <label style="font-weight:700; display:block; margin-bottom:8px; font-size:0.9rem; color:var(--primary-dark);">➕ เพิ่มผู้อนุมัติใหม่</label>
          <div style="display:flex; gap:10px;">
            <input type="text" id="new-payor-name" placeholder="ระบุชื่อจริง-นามสกุล ผู้จ่ายเงิน" style="flex:1; padding:8px 12px; border-radius:var(--border-radius-sm); border:1px solid var(--gray-300); font-size:0.85rem;">
            <button class="btn btn-primary" onclick="addNewPayorTrigger()" style="padding:8px 16px; font-size:0.85rem;">เพิ่ม</button>
          </div>
        </div>

        <label style="font-weight:700; display:block; margin-bottom:10px; font-size:0.9rem; color:var(--primary-dark);">รายชื่อผู้อนุมัติในระบบทั้งหมด</label>
        <div id="payors-list-container" style="max-height: 250px; overflow-y:auto; padding-right:5px;">
          ${listHTML}
        </div>
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" onclick="document.getElementById('payors-management-modal-overlay').classList.remove('active')">ปิดหน้าต่าง</button>
      </div>
    </div>
  `;

  modalOverlay.classList.add('active');
};

async function addNewPayorTrigger() {
  const nameInput = document.getElementById('new-payor-name');
  const name = nameInput?.value.trim();
  if (!name) {
    toast.warning("โปรดระบุชื่อผู้อนุมัติที่ถูกต้อง!");
    return;
  }

  const payorsList = state.activeCompanySettings.authorizedPayors || [];
  if (payorsList.includes(name)) {
    toast.warning("มีรายชื่อผู้อนุมัตินี้ในระบบอยู่แล้ว!");
    return;
  }

  payorsList.push(name);
  state.activeCompanySettings.authorizedPayors = payorsList;

  try {
    await saveCompanySettings(state.activeCompanyId, state.activeCompanySettings);
    nameInput.value = '';
    toast.success("เพิ่มรายชื่อผู้อนุมัติสำเร็จ!");
    // Refresh modal lists
    showManagePayorsModal();
    // Refresh Reports page dropdown
    if (state.activePage === 'reports') {
      if (window.checkPermission('export_transfer_report')) switchReportTab('bank');
      else if (window.checkPermission('export_sso_report')) switchReportTab('ss');
      else if (window.checkPermission('export_tax_report')) switchReportTab('tax');
      else if (window.checkPermission('export_kt26k_report')) switchReportTab('kt26');
      else document.getElementById('report-view-container').innerHTML = '<div class="glass-card text-center"><h4 style="color:var(--text-sub);">คุณไม่มีสิทธิ์เข้าถึงหน้ารายงานใดๆ</h4></div>';
    }
  } catch (err) {
    console.error(err);
    toast.error("เกิดข้อผิดพลาดในการบันทึกรายชื่อ: " + (err.message || ''));
  }
};

async function deletePayorItem(idx) {
  const payorsList = state.activeCompanySettings.authorizedPayors || [];
  const deletedPayor = payorsList[idx];

  const confirmed = await showConfirm({
    title: 'ยืนยันลบรายชื่อผู้อนุมัติ',
    message: `คุณต้องการลบรายชื่อ "${deletedPayor}" ออกจากระบบใช่หรือไม่?`,
    confirmText: '🗑️ ลบรายชื่อ',
    isDanger: true
  });
  if (!confirmed) return;

  payorsList.splice(idx, 1);
  state.activeCompanySettings.authorizedPayors = payorsList;

  // Clear selection if deleted
  if (state.selectedPayor === deletedPayor) {
    state.selectedPayor = "";
  }

  try {
    await saveCompanySettings(state.activeCompanyId, state.activeCompanySettings);
    toast.success("ลบรายชื่อเรียบร้อยแล้ว");
    showManagePayorsModal();
    if (state.activePage === 'reports') {
      if (window.checkPermission('export_transfer_report')) switchReportTab('bank');
      else if (window.checkPermission('export_sso_report')) switchReportTab('ss');
      else if (window.checkPermission('export_tax_report')) switchReportTab('tax');
      else if (window.checkPermission('export_kt26k_report')) switchReportTab('kt26');
      else document.getElementById('report-view-container').innerHTML = '<div class="glass-card text-center"><h4 style="color:var(--text-sub);">คุณไม่มีสิทธิ์เข้าถึงหน้ารายงานใดๆ</h4></div>';
    }
  } catch (err) {
    console.error(err);
    toast.error("เกิดข้อผิดพลาดในการลบรายชื่อ");
  }
};


// ==========================================================================
function renderPermissionsPage() {
  document.getElementById('page-display-title').textContent = 'ตั้งค่าสิทธิ์ (Permissions)';
  document.getElementById('page-display-desc').textContent = 'จัดการสิทธิ์การใช้งานสำหรับแต่ละตำแหน่ง (Role-Based Access Control)';

  const wrapper = document.getElementById('page-content-wrapper');

  const permDefinitions = [
    {
      category: '🗂️ การเข้าถึงและการใช้งานทั่วไป', items: [
        { key: 'access_all_companies', label: 'เข้าถึงและสลับดูข้อมูลได้ทุกบริษัท', type: 'checkbox' },
        { key: 'view_dashboard', label: 'เข้าดูหน้าแรก (Dashboard)', type: 'checkbox' },
        { key: 'view_calendar', label: 'เข้าดูหน้าปฏิทินบริษัท', type: 'checkbox' },
        { key: 'view_others_leave_calendar', label: 'เห็นวันหยุดและการลางานของพนักงานคนอื่น', type: 'checkbox' }
      ]
    },
    {
      category: '👥 การจัดการพนักงาน', items: [
        { key: 'view_employees', label: 'ดูรายชื่อและข้อมูลพื้นฐานพนักงาน', type: 'checkbox' },
        { key: 'add_employee', label: 'เพิ่มพนักงานใหม่', type: 'checkbox' },
        { key: 'delete_employee', label: 'ลบพนักงานออกจากระบบ', type: 'checkbox' },
        { key: 'reorder_employees', label: 'จัดเรียงลำดับพนักงาน', type: 'checkbox' },
        { key: 'manage_employee_files', label: 'จัดการไฟล์เอกสารแนบพนักงาน', type: 'checkbox' },
        { key: 'manage_disciplinary', label: 'จัดการประวัติการลงโทษพนักงาน', type: 'checkbox' },
        { key: 'edit_employee_general', label: 'แก้ไขข้อมูลทั่วไปของพนักงาน (ชื่อ, แผนก, กะงาน, บัญชี)', type: 'checkbox' },
        { key: 'edit_employee_salary', label: 'แก้ไขข้อมูลเงินเดือนของพนักงาน (ฐานเงินเดือน)', type: 'checkbox' },
        { key: 'view_employee_tenure', label: 'ดูข้อมูลอายุงาน', type: 'checkbox' },
        { key: 'view_employee_salary_increase', label: 'ดูวันที่ปรับฐานเงินเดือนล่าสุด', type: 'checkbox' },
        { key: 'view_executive_salary', label: 'สามารถดูข้อมูลเงินเดือนพนักงานแผนกบริหารได้', type: 'checkbox' }
      ]
    },
    {
      category: '⏰ การจัดการเวลาและเงินเดือน', items: [
        { key: 'view_timesheet', label: 'ดูตารางลงเวลาและยอดเงินเดือนปัจจุบัน', type: 'checkbox' },
        { key: 'edit_timesheet', label: 'ลงเวลาเข้า-ออกงานปัจจุบัน', type: 'checkbox' },
        { key: 'edit_payroll_adjustments', label: 'ปรับแต่งรายการรับ-หัก (Overrides)', type: 'checkbox' },
        { key: 'lock_timesheet', label: 'ล็อกประวัติ (Lock)', type: 'checkbox' },
        { key: 'unlock_timesheet', label: 'ปลดล็อกประวัติ (Unlock)', type: 'checkbox' },
        { key: 'view_past_months_limit', label: 'สิทธิ์ดูย้อนหลัง (จำนวนเดือน)', type: 'number' },
        { key: 'edit_past_months_limit', label: 'สิทธิ์แก้ไขย้อนหลัง (จำนวนเดือน)', type: 'number' },
        { key: 'view_future_months_limit', label: 'สิทธิ์ดูล่วงหน้า (จำนวนเดือน)', type: 'number' },
        { key: 'edit_future_months_limit', label: 'สิทธิ์แก้ไขล่วงหน้า (จำนวนเดือน)', type: 'number' }
      ]
    },
    {
      category: '📊 รายงาน', items: [
        { key: 'view_reports', label: 'เข้าถึงหน้ารายงาน', type: 'checkbox' },
        { key: 'export_payslips', label: 'พิมพ์สลิปเงินเดือน', type: 'checkbox' },
        { key: 'export_transfer_report', label: 'มีสิทธิ์เข้าถึงรายงานสรุปโอนเงิน', type: 'checkbox' },
        { key: 'export_sso_report', label: 'มีสิทธิ์เข้าถึงรายงานประกันสังคม (สปส.1-10)', type: 'checkbox' },
        { key: 'export_tax_report', label: 'มีสิทธิ์เข้าถึงรายงานภาษีหัก ณ ที่จ่าย (ภ.ง.ด.1)', type: 'checkbox' },
        { key: 'export_kt26k_report', label: 'มีสิทธิ์เข้าถึงแบบ กท.26ก', type: 'checkbox' }
      ]
    },
    {
      category: '⚙️ การตั้งค่าระบบ', items: [
        { key: 'manage_company_settings', label: 'จัดการข้อมูลและตั้งค่าระบบบริษัท', type: 'checkbox' },
        { key: 'manage_holidays', label: 'ตั้งค่าปฏิทิน วันหยุด (ปัจจุบัน/อนาคต)', type: 'checkbox' },
        { key: 'manage_retroactive_holidays', label: 'ตั้งค่าวันหยุดย้อนหลัง', type: 'checkbox' }
      ]
    },
    {
      category: '🛡️ การตั้งค่าสิทธิ์ (ล็อกเฉพาะ Superadmin)', items: [
        { key: 'manage_roles_permissions', label: 'แก้ไขตารางสิทธิ์การใช้งาน (หน้านี้)', type: 'checkbox' },
        { key: 'assign_employee_roles', label: 'เปลี่ยนระดับสิทธิ์พนักงาน', type: 'checkbox' }
      ]
    }
  ];

  let html = `
    <div style="background: var(--white); border-radius: var(--border-radius-lg); padding: 25px; box-shadow: var(--shadow); margin-bottom: 20px;">
      <h3 style="margin-top:0; color: var(--primary-dark); margin-bottom: 20px;">ตารางสิทธิ์การใช้งาน (Role Permissions Matrix)</h3>
      <p style="color: var(--text-sub); font-size: 0.9rem; margin-bottom: 20px;">ติ๊กถูกในช่องที่ต้องการอนุญาตให้สิทธิ์ สำหรับช่องที่เป็นสิทธิ์จำนวนเดือนย้อนหลัง/ล่วงหน้า ให้กรอกตัวเลข (เช่น 3, 6, 12, ใส่ 99 = ไม่จำกัด, ใส่ 0 = ห้ามดู/ห้ามแก้ไข)</p>
      
      <div style="overflow-x: auto;">
        <table style="width: 100%; border-collapse: collapse; min-width: 700px; font-size: 0.9rem;">
          <thead>
            <tr style="background: var(--light-bg); border-bottom: 2px solid var(--gray-300);">
              <th style="padding: 12px; text-align: left; width: 40%;">หัวข้อสิทธิ์ (Permissions)</th>
              <th style="padding: 12px; text-align: center; width: 20%; color: var(--primary-dark);">Superadmin</th>
              <th style="padding: 12px; text-align: center; width: 20%; color: var(--primary);">Admin</th>
              <th style="padding: 12px; text-align: center; width: 20%; color: var(--text-color);">Member</th>
            </tr>
          </thead>
          <tbody>
  `;

  permDefinitions.forEach(group => {
    html += `
      <tr>
        <td colspan="4" style="padding: 12px; background: var(--gray-100); font-weight: bold; border-bottom: 1px solid var(--gray-300);">${group.category}</td>
      </tr>
    `;
    group.items.forEach(item => {
      const renderControl = (role, key, type) => {
        // Only superadmin can edit role permissions
        if (key === 'manage_roles_permissions' || key === 'assign_employee_roles') {
          if (role !== 'superadmin') {
            return `<span style="color: var(--gray-400);">🔒 ไม่อนุญาต</span>`;
          }
        }

        const currentVal = state.rolePermissions[role][key];

        if (type === 'checkbox') {
          return `<input type="checkbox" id="perm-${role}-${key}" ${currentVal ? 'checked' : ''} style="transform: scale(1.2); cursor: pointer;">`;
        } else if (type === 'number') {
          return `<input type="number" id="perm-${role}-${key}" value="${currentVal}" min="0" max="99" style="width: 60px; text-align: center; padding: 4px; border: 1px solid var(--gray-300); border-radius: 4px;">`;
        }
      };

      html += `
        <tr style="border-bottom: 1px solid var(--gray-200);">
          <td style="padding: 12px; padding-left: 24px;">${item.label}</td>
          <td style="padding: 12px; text-align: center;">${renderControl('superadmin', item.key, item.type)}</td>
          <td style="padding: 12px; text-align: center;">${renderControl('admin', item.key, item.type)}</td>
          <td style="padding: 12px; text-align: center;">${renderControl('member', item.key, item.type)}</td>
        </tr>
      `;
    });
  });

  html += `
          </tbody>
        </table>
      </div>
      
      <div style="margin-top: 30px; text-align: right;">
        <button class="btn btn-primary" onclick="savePermissions()" style="padding: 10px 30px; font-size: 1rem;">💾 บันทึกการตั้งค่าสิทธิ์</button>
      </div>
    </div>
  `;

  wrapper.innerHTML = html;
};

async function savePermissions() {
  const btn = document.querySelector('button[onclick="savePermissions()"]');
  btn.disabled = true;
  btn.textContent = 'กำลังบันทึก...';

  try {
    const roles = ['superadmin', 'admin', 'member'];
    const newPerms = JSON.parse(JSON.stringify(state.rolePermissions)); // Deep copy current state

    // Loop through DOM and collect values
    document.querySelectorAll('input[id^="perm-"]').forEach(input => {
      const parts = input.id.split('-'); // e.g. perm-admin-view_dashboard
      const role = parts[1];
      const key = parts.slice(2).join('-'); // re-join in case key has dashes, though we use underscores

      if (input.type === 'checkbox') {
        newPerms[role][key] = input.checked;
      } else if (input.type === 'number') {
        newPerms[role][key] = parseInt(input.value) || 0;
      }
    });

    // Always enforce superadmin having role management
    newPerms.superadmin.manage_roles_permissions = true;
    newPerms.superadmin.assign_employee_roles = true;
    // Always enforce admin/member NOT having role management
    newPerms.admin.manage_roles_permissions = false;
    newPerms.admin.assign_employee_roles = false;
    newPerms.member.manage_roles_permissions = false;
    newPerms.member.assign_employee_roles = false;

    // Save to Firebase
    const docRef = doc(db, 'globalSettings', 'permissions');
    await setDoc(docRef, newPerms);

    // Update local state
    state.rolePermissions = newPerms;

    alert('บันทึกการตั้งค่าสิทธิ์เรียบร้อยแล้ว');
  } catch (err) {
    console.error(err);
    alert('เกิดข้อผิดพลาดในการบันทึก: ' + err.message);
  } finally {
    btn.disabled = false;
    btn.textContent = '💾 บันทึกการตั้งค่าสิทธิ์';
  }
};



// Bind all settings functions to window for backwards compatibility
if (typeof window !== 'undefined') {
  window.renderSettingsPage = renderSettingsPage;
  window.toggleDayWorkHours = toggleDayWorkHours;
  window.toggleDiligenceAllowanceSection = toggleDiligenceAllowanceSection;
  window.updateSelectedPayor = updateSelectedPayor;
  window.showManagePayorsModal = showManagePayorsModal;
  window.addNewPayorTrigger = addNewPayorTrigger;
  window.deletePayorItem = deletePayorItem;
  window.renderPermissionsPage = renderPermissionsPage;
  window.savePermissions = savePermissions;
}

export {
  renderSettingsPage,
  toggleDayWorkHours,
  toggleDiligenceAllowanceSection,
  updateSelectedPayor,
  showManagePayorsModal,
  addNewPayorTrigger,
  deletePayorItem,
  renderPermissionsPage,
  savePermissions
};
